import { memoize } from 'lodash';
import {
  Source,
  empty,
  fromArray,
  fromPromise,
  make,
  map,
  merge,
  mergeMap,
  pipe,
} from 'wonka';
import {
  ModelsMap,
  ShapesMap,
  SheetCalibrationsMap,
  SheetShapesMap,
  SparkelPropertiesMap,
} from '../components/common/ForgeViewer';
import {
  ElementIdentifierDbidType,
  ShapeDeepFragment,
  SheetScaleType,
  SheetShapeDeepFragment,
} from '../gql/graphql';
import {
  CustomElementClass,
  getMappingForTargetType,
  IfcClass,
  RevitCategory,
} from './geometry/algorithm-map';

import { getGeometricAlgorithmForType } from './geometry/algorithm-selector';
import {
  CornerMultiPolygon2Algorithm,
  ExtrudedPolygonAlgorithm,
  GeometricAlgorithmType,
  MeshAlgorithm,
  MeshAlgorithmType,
  MultiPolygon2Algorithm,
  MultiPolygon2AlgorithmType,
  MultiPolygonAlgorithm,
  ParameterizedMultiPolygon2Algorithm,
  Point2Algorithm,
  PointAlgorithm,
  Polyline2Algorithm,
  Polyline2AlgorithmType,
  PolylineAlgorithm,
  RandMultiPolygon2Algorithm,
  SlopedInsulationAlgorithm,
  isExtrudedPolygonAlgorithm,
  isMeshAlgorithm,
  isPoint2Algorithm,
  isPointAlgorithm,
  isPolygon2Algorithm,
  isPolygonAlgorithm,
  isPolyline2Algorithm,
  isPolylineAlgorithm,
} from './geometry/algorithm-types';
import { getScaledValue } from './geometry/algorithms/util';
import {
  toMultiPolygon,
  toMultiPolygon2,
  toPlane,
  toPoints,
  toPoints2,
} from './geometry/algorithms/util/type-mapping';
import { getTriangles } from './geometry/extract-triangles';
import {
  GeometricProperty,
  GeometricResult,
  LocationProperty,
  OperationContext,
  OperationResult,
  Point,
  Point2,
  ResultType,
  ScaledGeometricResult,
  dimensionsForResultType,
  isGeometricProperty,
  isLocationProperty,
} from './geometry/geometric-types';
import {
  MeshAlgorithmResult,
  MeshAlgorithmResultsMap,
  PropertyContext,
} from './property-operations';
import { getUnitScaleFromCalibration } from './sheet-calibration';
import { getWorkerPool } from './worker/worker-pool';
import { SlopedInsulationCrossSection } from 'src/components/orderContractor/pdf-shapes/SheetShapeDrawing';

export const calculateGeometry = async (
  geometricPropertyOrAlgorithm:
    | GeometricProperty
    | GeometricAlgorithmType
    | LocationProperty,
  dbIds: ElementIdentifierDbidType[],
  propertyContext: PropertyContext,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  }
): Promise<OperationResult[]> => {
  const {
    loadedModels,
    loadedShapes,
    loadedSheetShapes,
    loadedSheetCalibrations,
    loadedSparkelProperties,
    meshAlgorithmResults,
  } = propertyContext;
  const { bimElementDbIds, shapeDbIds, sheetShapeDbIds } = splitDbIdsByType(
    dbIds,
    loadedShapes,
    loadedModels,
    loadedSheetShapes
  );

  const bimResults = await calculateGeometryForMeshElements(
    geometricPropertyOrAlgorithm,
    bimElementDbIds,
    loadedModels,
    projectSettings,
    meshAlgorithmResults
  );

  const shapeResults = calculateGeometryForShapes(
    geometricPropertyOrAlgorithm,
    shapeDbIds,
    loadedShapes,
    projectSettings
  );

  const sheetShapeResults = calculateGeometryForSheetShapes(
    geometricPropertyOrAlgorithm,
    sheetShapeDbIds,
    loadedSheetShapes,
    loadedSheetCalibrations,
    loadedSparkelProperties
  );

  return [...bimResults, ...shapeResults, ...sheetShapeResults];
};

function splitDbIdsByType(
  dbIds: ElementIdentifierDbidType[],
  loadedShapes: ShapesMap,
  loadedModels: ModelsMap,
  loadedSheetShapes: SheetShapesMap
) {
  const shapeDbIds: ElementIdentifierDbidType[] = [];
  const sheetShapeDbIds: ElementIdentifierDbidType[] = [];
  const bimElementDbIds: ElementIdentifierDbidType[] = [];
  dbIds.forEach((dbId) => {
    if (dbId.modelUrn in loadedShapes) {
      shapeDbIds.push(dbId);
    } else if (dbId.modelUrn in loadedModels) {
      bimElementDbIds.push(dbId);
    } else if (dbId.modelUrn in loadedSheetShapes) {
      sheetShapeDbIds.push(dbId);
    }
  });
  return { bimElementDbIds, shapeDbIds, sheetShapeDbIds };
}

async function calculateGeometryForMeshElements(
  geometricPropertyOrAlgorithm:
    | GeometricProperty
    | GeometricAlgorithmType
    | LocationProperty,
  meshElementDbIds: ElementIdentifierDbidType[],
  loadedModels: ModelsMap,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  },
  meshAlgorithmResults?: MeshAlgorithmResultsMap
): Promise<OperationResult[]> {
  const dbidsWithCategories = await getCategories(
    loadedModels,
    meshElementDbIds
  );
  const results: OperationResult[] = [];

  // Track stats for logging
  let precomputedCount = 0;
  let calculatedCount = 0;
  let failedCount = 0;

  for (const dbId of dbidsWithCategories) {
    const model = loadedModels[dbId.modelUrn];
    const algorithmType = getAlgorithmTypeForBimElement(
      geometricPropertyOrAlgorithm,
      dbId
    );

    if (model && algorithmType) {
      // Try to use precomputed value first
      const precomputedResult = tryGetPrecomputedResult(
        model,
        dbId,
        algorithmType,
        meshAlgorithmResults
      );

      if (precomputedResult) {
        results.push(precomputedResult);
        precomputedCount++;
        continue;
      }

      // Fall back to frontend calculation
      try {
        const result = await calculateGeometricAlgorithmForMeshElement(
          algorithmType,
          dbId,
          model,
          projectSettings
        );
        if (result) {
          results.push(result);
          calculatedCount++;
        }
      } catch (error: unknown) {
        //Don't add result to result list if error and log. Triggers missing quantity warnings.
        console.error('Geometric calculation failed for DB id ', dbId, error);
        failedCount++;
      }
    }
  }

  // Log summary of where values came from
  if (precomputedCount > 0 || calculatedCount > 0) {
    console.log(
      `[Geometry] Algorithm "${geometricPropertyOrAlgorithm}": ` +
        `precomputed=${precomputedCount}, calculated=${calculatedCount}, failed=${failedCount}`
    );
  }

  return results;
}

/** Extract target type from dbId object */
function getTargetType(
  dbId: DbIdWithIfcClass | DbIdWithRevitCategory
): IfcClass | RevitCategory | undefined {
  return (
    (dbId as DbIdWithIfcClass).ifcClass ||
    (dbId as DbIdWithRevitCategory).revitCategory ||
    undefined
  );
}

/** Build ScaledGeometricResult from precomputed algorithm result */
function toScaledResult(
  algorithmResult: MeshAlgorithmResult
): ScaledGeometricResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = algorithmResult as any;
  return {
    value: algorithmResult.value,
    units: algorithmResult.unit,
    type: r.type ?? ResultType.NUMBER,
    ...(r.multiPolyline && { multiPolyline: r.multiPolyline }),
    ...(r.triangles && { triangles: r.triangles }),
    ...(r.multiPolygon && { multiPolygon: r.multiPolygon, plane: r.plane }),
    ...(r.point && { point: r.point }),
  };
}

/**
 * Try to get a precomputed mesh algorithm result for an element.
 * Uses model.reverseMapDbId to convert viewer dbIds to OBJ dbIds.
 */
function tryGetPrecomputedResult(
  model: Autodesk.Viewing.Model,
  dbId: DbIdWithIfcClass | DbIdWithRevitCategory,
  algorithmType: GeometricAlgorithmType,
  meshAlgorithmResults?: MeshAlgorithmResultsMap
): OperationResult | undefined {
  const modelResults = meshAlgorithmResults?.[dbId.modelUrn];
  if (!modelResults) {
    return undefined;
  }

  const algorithmResult = modelResults[String(dbId.dbId)]?.[algorithmType];
  if (!algorithmResult) {
    return undefined;
  }

  const targetType = getTargetType(dbId);
  if (!targetType) {
    return undefined;
  }

  const context: OperationContext = {
    dbId: dbId.dbId,
    targetType,
    unitScale: model.getUnitScale(),
    modelUrn: dbId.modelUrn,
    algorithmType,
  };

  return [toScaledResult(algorithmResult), context];
}

function calculateGeometryForShapes(
  geometricPropertyOrAlgorithm:
    | GeometricProperty
    | GeometricAlgorithmType
    | LocationProperty,
  shapeDbIds: ElementIdentifierDbidType[],
  loadedShapes: ShapesMap,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  }
): OperationResult[] {
  const results: OperationResult[] = [];
  for (const dbId of shapeDbIds) {
    const shapes = loadedShapes[dbId.modelUrn]?.filter((shape) =>
      dbId.dbIds.includes(shape.dbId)
    );
    if (!shapes) {
      continue;
    }

    for (let shape of shapes) {
      const algorithmType = getAlgorithmTypeForShape(
        geometricPropertyOrAlgorithm,
        shape
      );
      if (!algorithmType) {
        continue;
      }
      const result = calculateGeometricAlgorithmForShape(
        algorithmType,
        dbId,
        shape,
        projectSettings
      );
      if (result) {
        results.push(result);
      }
    }
  }
  return results;
}

function calculateGeometryForSheetShapes(
  geometricPropertyOrAlgorithm:
    | GeometricProperty
    | GeometricAlgorithmType
    | LocationProperty,
  sheetShapeDbIds: ElementIdentifierDbidType[],
  loadedSheetShapes: SheetShapesMap,
  loadedSheetCalibrations: SheetCalibrationsMap,
  loadedSparkelProperties: SparkelPropertiesMap
): OperationResult[] {
  const results: OperationResult[] = [];
  for (const dbId of sheetShapeDbIds) {
    const shapes = loadedSheetShapes[dbId.modelUrn]?.filter((shape) =>
      dbId.dbIds.includes(shape.dbId)
    );
    if (!shapes) {
      continue;
    }

    const externalShapes = loadedSheetShapes[dbId.modelUrn];

    for (let shape of shapes) {
      const calibrationForShape = loadedSheetCalibrations[shape.sheetId]?.find(
        (calibration) => calibration.pageNumber === shape.sheetPageNumber
      )?.calibration;

      const algorithmType = getAlgorithmTypeForSheetShape(
        geometricPropertyOrAlgorithm,
        shape
      );
      if (!algorithmType || !calibrationForShape) {
        continue;
      }

      const result = calculateGeometricAlgorithmForSheetShape(
        algorithmType,
        dbId,
        shape,
        calibrationForShape,
        loadedSparkelProperties,
        externalShapes
      );
      if (result) {
        results.push(result);
      }
    }
  }
  return results;
}

// Don't calculate geometric algorithms for elements with a high number of triangles
// As these elements are typically composite/complex elements users are not interested in
// and they can cause the application to become unresponsive
const maxTrianglesForMeshElement = 50000;
const calculateGeometricAlgorithmForMeshElement = memoize(
  async (
    algorithmType: GeometricAlgorithmType,
    dbId: DbIdWithIfcClass | DbIdWithRevitCategory,
    model: Autodesk.Viewing.Model,
    projectSettings: {
      maxTrianglesForComplexAlgorithm: number;
      globalOffset: Point;
    }
  ): Promise<OperationResult | undefined> => {
    if (!isMeshAlgorithm(algorithmType)) {
      return;
    }
    let targetType: IfcClass | RevitCategory;
    if ((dbId as DbIdWithIfcClass).ifcClass) {
      targetType = (dbId as DbIdWithIfcClass).ifcClass;
    } else if ((dbId as DbIdWithRevitCategory).revitCategory) {
      targetType = (dbId as DbIdWithRevitCategory).revitCategory;
    } else {
      return;
    }

    const context: OperationContext = {
      dbId: dbId.dbId,
      targetType,
      unitScale: model.getUnitScale(),
      modelUrn: dbId.modelUrn,
      algorithmType: algorithmType,
    };
    const trianglesForDbId = getTriangles(model, dbId.dbId);
    if (trianglesForDbId.length > maxTrianglesForMeshElement) {
      return [undefined, context];
    }
    const pool = getWorkerPool();
    if (!pool) {
      const algorithm = getGeometricAlgorithmForType(
        algorithmType,
        projectSettings
      ) as MeshAlgorithm;
      const result = algorithm(trianglesForDbId);
      return toOperationResult(result, context);
    }
    try {
      const result = await pool.queue((worker) =>
        worker.evaluateMeshAlgorithm(trianglesForDbId, algorithmType)
      );
      return toOperationResult(result, context);
    } catch (err: unknown) {
      throw new Error('Worker task failed', err as Error);
    }
  },
  (algorithmType, dbId, _, projectSettings) =>
    `${JSON.stringify(dbId)}-${algorithmType}-${JSON.stringify(
      projectSettings
    )}`
);

function calculateGeometricAlgorithmForShape(
  algorithmType: GeometricAlgorithmType,
  dbId: ElementIdentifierDbidType,
  shape: ShapeDeepFragment,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  }
): OperationResult | undefined {
  const { polygon, extrudedPolygon, line } = shape;

  if (polygon && isPolygonAlgorithm(algorithmType)) {
    const algorithm = getGeometricAlgorithmForType(
      algorithmType,
      projectSettings
    ) as MultiPolygonAlgorithm;
    const { plane, multipolygon, unitScale } = polygon;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.PolygonShape,
      unitScale,
      modelUrn: dbId.modelUrn,
      algorithmType,
    };

    const result = algorithm(toMultiPolygon(multipolygon), toPlane(plane));
    return toOperationResult(result, context);
  } else if (extrudedPolygon && isExtrudedPolygonAlgorithm(algorithmType)) {
    const algorithm = getGeometricAlgorithmForType(
      algorithmType,
      projectSettings
    ) as ExtrudedPolygonAlgorithm;
    const { plane, multipolygon, unitScale, thickness } = extrudedPolygon;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.ExtrudedPolygonShape,
      unitScale,
      modelUrn: dbId.modelUrn,
      algorithmType,
    };

    const result = algorithm(
      toMultiPolygon(multipolygon),
      toPlane(plane),
      thickness
    );
    return toOperationResult(result, context);
  } else if (line && isPolylineAlgorithm(algorithmType)) {
    const algorithm = getGeometricAlgorithmForType(
      algorithmType,
      projectSettings
    ) as PolylineAlgorithm;

    const { points, unitScale } = line;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.LineShape,
      unitScale,
      modelUrn: dbId.modelUrn,
      algorithmType,
    };
    const result = algorithm(toPoints(points));
    return toOperationResult(result, context);
  } else if (shape.shapePoint && isPointAlgorithm(algorithmType)) {
    const algorithm = getGeometricAlgorithmForType(
      algorithmType,
      projectSettings
    ) as PointAlgorithm;
    const { point } = shape.shapePoint;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.PointShape,
      unitScale: 0.3048, // Convert from feet to meters
      modelUrn: dbId.modelUrn,
      algorithmType,
    };
    const result = algorithm([point.x, point.y, point.z]);
    return toOperationResult(result, context);
  }
}

// eslint-disable-next-line complexity
function calculateGeometricAlgorithmForSheetShape(
  algorithmType: GeometricAlgorithmType,
  dbId: ElementIdentifierDbidType,
  shape: SheetShapeDeepFragment,
  calibration: SheetScaleType,
  loadedSparkelProperties: SparkelPropertiesMap,
  externalShapes: SheetShapeDeepFragment[]
): OperationResult | undefined {
  //Parameterize here, remember units. Roof parameters.
  //Make sure to split to seperate components
  //Maybe super specialcase protan here?? Not that many shapes, can afford one network request per element. Not that many

  const { sheetShapePolygon: polygon, sheetShapeLine: line } = shape;

  if (
    polygon &&
    isPolygon2Algorithm(algorithmType) &&
    algorithmType !== MultiPolygon2AlgorithmType.POLYGON2_FIRE_SECTIONING &&
    algorithmType !== MultiPolygon2AlgorithmType.POLYGON2_CORNER_AREA &&
    algorithmType !== MultiPolygon2AlgorithmType.POLYGON2_RAND_AREA
  ) {
    const algorithm = getGeometricAlgorithmForType(
      algorithmType
    ) as MultiPolygon2Algorithm;
    const { multipolygon } = polygon;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.Polygon2Shape,
      unitScale: getUnitScaleFromCalibration(calibration),
      modelUrn: dbId.modelUrn,
      algorithmType,
    };

    const result = algorithm(toMultiPolygon2(multipolygon));
    return toOperationResult(result, context);
  } else if (
    line &&
    isPolyline2Algorithm(algorithmType) &&
    algorithmType !== Polyline2AlgorithmType.LINE2_CROSS_SECTION_BASELINE_LENGTH
  ) {
    const algorithm = getGeometricAlgorithmForType(
      algorithmType
    ) as Polyline2Algorithm;

    const { points } = line;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.Line2Shape,
      unitScale: getUnitScaleFromCalibration(calibration),
      modelUrn: dbId.modelUrn,
      algorithmType,
    };
    const result = algorithm(toPoints2(points));
    return toOperationResult(result, context);
  } else if (
    polygon &&
    algorithmType === MultiPolygon2AlgorithmType.POLYGON2_FIRE_SECTIONING
  ) {
    //Better handling above, can likely be refactored to a switch
    const algorithm = getGeometricAlgorithmForType(
      algorithmType
    ) as ParameterizedMultiPolygon2Algorithm;

    const { multipolygon } = polygon;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.Polygon2Shape,
      unitScale: getUnitScaleFromCalibration(calibration),
      modelUrn: dbId.modelUrn,
      algorithmType,
    };

    const relevantSparkelProperties = loadedSparkelProperties[dbId.modelUrn];
    let fireSectioningBuffer = 0;

    if (relevantSparkelProperties) {
      const bufferFromProps = relevantSparkelProperties.find(
        (prop) =>
          prop.propertySet === 'Fire sectioning' && prop.dbId === shape.dbId
      )?.propertyValue;
      if (bufferFromProps) {
        fireSectioningBuffer = Number(bufferFromProps);
      }
    }

    const result = algorithm(
      toMultiPolygon2(multipolygon),
      Number(fireSectioningBuffer / context.unitScale),
      externalShapes,
      getUnitScaleFromCalibration(calibration)
    );
    return toOperationResult(result, context);
  } else if (
    polygon &&
    algorithmType === MultiPolygon2AlgorithmType.POLYGON2_CORNER_AREA
  ) {
    //Better handling above
    const algorithm = getGeometricAlgorithmForType(
      algorithmType
    ) as CornerMultiPolygon2Algorithm;

    const { multipolygon } = polygon;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.Polygon2Shape,
      unitScale: getUnitScaleFromCalibration(calibration),
      modelUrn: dbId.modelUrn,
      algorithmType,
    };

    const relevantSparkelProperties = loadedSparkelProperties[dbId.modelUrn];
    let roofHeight;

    if (relevantSparkelProperties) {
      const bufferFromProps = relevantSparkelProperties.find(
        (prop) => prop.propertySet === 'Roof height' && prop.dbId === shape.dbId
      )?.propertyValue;
      if (bufferFromProps) {
        roofHeight = Number(bufferFromProps);
      }
    }

    const result = algorithm(
      toMultiPolygon2(multipolygon),
      roofHeight,
      context.unitScale
    );
    return toOperationResult(result, context);
  } else if (
    polygon &&
    algorithmType === MultiPolygon2AlgorithmType.POLYGON2_RAND_AREA
  ) {
    //Better handling above
    const algorithm = getGeometricAlgorithmForType(
      algorithmType
    ) as RandMultiPolygon2Algorithm;

    const { multipolygon } = polygon;
    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.Polygon2Shape,
      unitScale: getUnitScaleFromCalibration(calibration),
      modelUrn: dbId.modelUrn,
      algorithmType,
    };

    const relevantSparkelProperties = loadedSparkelProperties[dbId.modelUrn];
    let roofHeight;

    if (relevantSparkelProperties) {
      const bufferFromProps = relevantSparkelProperties.find(
        (prop) => prop.propertySet === 'Roof height' && prop.dbId === shape.dbId
      )?.propertyValue;
      if (bufferFromProps) {
        roofHeight = Number(bufferFromProps);
      }
    }

    const result = algorithm(
      toMultiPolygon2(multipolygon),
      roofHeight,
      context.unitScale
    );
    return toOperationResult(result, context);
  } else if (
    shape.sheetShapeLine &&
    algorithmType === Polyline2AlgorithmType.LINE2_CROSS_SECTION_BASELINE_LENGTH
  ) {
    console.log('Calculating cross section baseline length');
    const algorithm = getGeometricAlgorithmForType(
      algorithmType
    ) as SlopedInsulationAlgorithm;

    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.Line2Shape,
      unitScale: getUnitScaleFromCalibration(calibration),
      modelUrn: dbId.modelUrn,
      algorithmType,
    };

    const relevantSparkelProperties = loadedSparkelProperties[dbId.modelUrn];
    let crossSection: SlopedInsulationCrossSection | undefined;

    if (relevantSparkelProperties) {
      const propertyValue = relevantSparkelProperties.find(
        (prop) =>
          prop.propertySet === 'CrossSection' && prop.dbId === shape.dbId
      )?.propertyValue;
      if (propertyValue) {
        const parsedCrossSection = JSON.parse(
          propertyValue
        ) as SlopedInsulationCrossSection;

        if (parsedCrossSection) {
          crossSection = parsedCrossSection;
        }
      }
    }

    if (!crossSection) {
      return;
    }

    const result = algorithm(crossSection);
    return toOperationResult(result, context);
  } else if (shape.sheetShapePoint && isPoint2Algorithm(algorithmType)) {
    const algorithm = getGeometricAlgorithmForType(
      algorithmType
    ) as Point2Algorithm;

    const { point } = shape.sheetShapePoint;

    const context: OperationContext = {
      dbId: shape.dbId,
      targetType: CustomElementClass.Point2Shape,
      unitScale: getUnitScaleFromCalibration(calibration),
      modelUrn: dbId.modelUrn,
      algorithmType,
    };

    const result = algorithm([point.x, point.y] as Point2);
    return toOperationResult(result, context);
  }
}

function getAlgorithmTypeForBimElement(
  geometricPropertyOrAlgorithm:
    | GeometricProperty
    | GeometricAlgorithmType
    | LocationProperty,
  dbId: DbIdWithIfcClass | DbIdWithRevitCategory
): GeometricAlgorithmType | undefined {
  if (
    isGeometricProperty(geometricPropertyOrAlgorithm) ||
    isLocationProperty(geometricPropertyOrAlgorithm)
  ) {
    // Check if the dbId is an IFC class or a Revit category
    if ((dbId as DbIdWithIfcClass).ifcClass !== undefined) {
      return getMappingForTargetType((dbId as DbIdWithIfcClass).ifcClass)?.get(
        geometricPropertyOrAlgorithm
      );
    } else if ((dbId as DbIdWithRevitCategory).revitCategory !== undefined) {
      return getMappingForTargetType(
        (dbId as DbIdWithRevitCategory).revitCategory
      )?.get(geometricPropertyOrAlgorithm);
    } else {
      console.error(
        'dbId does not contain a valid IFC class or Revit category'
      );
      return undefined;
    }
  } else {
    return geometricPropertyOrAlgorithm;
  }
}

function getAlgorithmTypeForShape(
  geometricPropertyOrAlgorithm:
    | GeometricProperty
    | GeometricAlgorithmType
    | LocationProperty,
  shape: ShapeDeepFragment
): GeometricAlgorithmType | undefined {
  if (
    isGeometricProperty(geometricPropertyOrAlgorithm) ||
    isLocationProperty(geometricPropertyOrAlgorithm)
  ) {
    if (shape.polygon) {
      return getMappingForTargetType(CustomElementClass.PolygonShape)?.get(
        geometricPropertyOrAlgorithm
      );
    } else if (shape.extrudedPolygon) {
      return getMappingForTargetType(
        CustomElementClass.ExtrudedPolygonShape
      )?.get(geometricPropertyOrAlgorithm);
    } else if (shape.line) {
      return getMappingForTargetType(CustomElementClass.LineShape)?.get(
        geometricPropertyOrAlgorithm
      );
    } else if (shape.shapePoint) {
      return getMappingForTargetType(CustomElementClass.PointShape)?.get(
        geometricPropertyOrAlgorithm
      );
    }
  } else {
    return geometricPropertyOrAlgorithm;
  }
}

function getAlgorithmTypeForSheetShape(
  geometricPropertyOrAlgorithm:
    | GeometricProperty
    | GeometricAlgorithmType
    | LocationProperty,
  shape: SheetShapeDeepFragment
): GeometricAlgorithmType | undefined {
  if (
    isGeometricProperty(geometricPropertyOrAlgorithm) ||
    isLocationProperty(geometricPropertyOrAlgorithm)
  ) {
    if (shape.sheetShapePolygon) {
      return getMappingForTargetType(CustomElementClass.Polygon2Shape)?.get(
        geometricPropertyOrAlgorithm
      );
    } else if (shape.sheetShapeLine) {
      return getMappingForTargetType(CustomElementClass.Line2Shape)?.get(
        geometricPropertyOrAlgorithm
      );
    } else if (shape.sheetShapePoint) {
      return getMappingForTargetType(CustomElementClass.Point2Shape)?.get(
        geometricPropertyOrAlgorithm
      );
    }
  } else {
    return geometricPropertyOrAlgorithm;
  }
}

/* This returns an Observable of geometric results for each dbId,
  this allows us to stream the results as they come in, instead of waiting for all of them to finish, which
  can cause the application to become unresponsive */
export const calculateGeometry$ = (
  geometricPropertyOrAlgorithm: GeometricProperty | MeshAlgorithmType,
  modelDbIds: ElementIdentifierDbidType[],
  propertyContext: PropertyContext,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  }
): Source<OperationResult> => {
  const {
    loadedModels,
    loadedShapes,
    loadedSheetShapes,
    loadedSparkelProperties,
  } = propertyContext;
  const { bimElementDbIds, shapeDbIds, sheetShapeDbIds } = splitDbIdsByType(
    modelDbIds,
    loadedShapes,
    loadedModels,
    loadedSheetShapes
  );

  const { meshAlgorithmResults } = propertyContext;

  const standardElementCalculations$ = pipe(
    fromArray(bimElementDbIds),
    mergeMap((modelDbId) =>
      pipe(
        getCategories$(
          propertyContext.loadedModels,
          modelDbId.modelUrn,
          modelDbId.dbIds
        ),
        mergeMap((dbId) => {
          const algorithmType: GeometricAlgorithmType | undefined =
            getAlgorithmTypeForBimElement(geometricPropertyOrAlgorithm, dbId);

          if (!algorithmType) {
            return empty;
          }

          const model = propertyContext.loadedModels[dbId.modelUrn];

          // Try precomputed result first (includes geometry for visualization)
          const precomputed = tryGetPrecomputedResult(
            model,
            dbId,
            algorithmType,
            meshAlgorithmResults
          );
          if (precomputed) {
            return fromArray([precomputed]);
          }

          // Fall back to frontend calculation
          return fromPromise(
            calculateGeometricAlgorithmForMeshElement(
              algorithmType,
              dbId,
              model,
              projectSettings
            )
          );
        })
      )
    )
  );

  const shapeCalculations$: Source<OperationResult> = pipe(
    fromArray(shapeDbIds),
    mergeMap((dbId) => {
      const shapes = propertyContext.loadedShapes[dbId.modelUrn]?.filter(
        (shape) => dbId.dbIds.includes(shape.dbId)
      );
      if (shapes === undefined) {
        return empty;
      }

      return pipe(
        fromArray(shapes),
        map((shape) => {
          const algorithmType = getAlgorithmTypeForShape(
            geometricPropertyOrAlgorithm,
            shape
          );
          if (!algorithmType) {
            return undefined;
          }
          return calculateGeometricAlgorithmForShape(
            algorithmType,
            dbId,
            shape,
            projectSettings
          );
        })
      );
    })
  );

  const sheetShapeCalculations$: Source<OperationResult> = pipe(
    fromArray(sheetShapeDbIds),
    mergeMap((dbId) => {
      const shapes = propertyContext.loadedSheetShapes[dbId.modelUrn]?.filter(
        (shape) => dbId.dbIds.includes(shape.dbId)
      );
      if (shapes === undefined) {
        return empty;
      }
      const externalShapes = loadedSheetShapes[dbId.modelUrn];

      return pipe(
        fromArray(shapes),
        map((shape) => {
          const algorithmType = getAlgorithmTypeForSheetShape(
            geometricPropertyOrAlgorithm,
            shape
          );
          const calibration = propertyContext.loadedSheetCalibrations[
            shape.sheetId
          ]?.find(
            (calibration) => calibration.pageNumber === shape.sheetPageNumber
          )?.calibration;

          if (!algorithmType || !calibration) {
            return undefined;
          }
          return calculateGeometricAlgorithmForSheetShape(
            algorithmType,
            dbId,
            shape,
            calibration,
            loadedSparkelProperties,
            externalShapes
          );
        })
      );
    })
  );

  return merge([
    standardElementCalculations$,
    shapeCalculations$,
    sheetShapeCalculations$,
  ]);
};

const getIfcClasses$ = (
  models: ModelsMap,
  modelUrn: string,
  dbIds: number[]
): Source<DbIdWithIfcClass> => {
  if (dbIds.length === 0) {
    // Because forge returns properties for all elements when no dbIds are specified
    return empty;
  }
  return make((observer) => {
    let cancelled = false;

    const model = models[modelUrn];
    model.getBulkProperties2(
      dbIds,
      {
        propFilter: ['IfcClass'],
        categoryFilter: ['Element'],
        ignoreHidden: true,
      },
      (properties: Autodesk.Viewing.PropertyResult[]) => {
        if (cancelled) {
          return;
        }
        for (const property of properties) {
          if (cancelled) {
            break;
          }
          if (property.properties.length === 0) {
            continue;
          }
          const ifcClass = property.properties[0].displayValue as IfcClass;
          observer.next({
            modelUrn: modelUrn,
            dbId: property.dbId,
            ifcClass: ifcClass,
          });
        }
        observer.complete();
      },
      () => observer.complete()
    );

    return () => {
      cancelled = true;
    };
  });
};

type DbIdWithRevitCategory = {
  dbId: number;
  modelUrn: string;
  revitCategory: RevitCategory;
};

const getRevitCategories$ = (
  models: ModelsMap,
  modelUrn: string,
  dbIds: number[]
): Source<DbIdWithRevitCategory> => {
  if (dbIds.length === 0) {
    // Because forge returns properties for all elements when no dbIds are specified
    return empty;
  }
  return make((observer) => {
    let cancelled = false;

    const model = models[modelUrn];
    model.getBulkProperties2(
      dbIds,
      {
        propFilter: ['Category'],
        categoryFilter: ['__category__'],
        ignoreHidden: false,
      },
      (properties: Autodesk.Viewing.PropertyResult[]) => {
        if (cancelled) {
          return;
        }
        for (const property of properties) {
          if (cancelled) {
            break;
          }
          if (property.properties.length === 0) {
            continue;
          }
          const revitCategory = property.properties[0]
            .displayValue as RevitCategory;
          observer.next({
            modelUrn: modelUrn,
            dbId: property.dbId,
            revitCategory,
          });
        }
        observer.complete();
      },
      () => observer.complete()
    );

    return () => {
      cancelled = true;
    };
  });
};

const getCategories$ = (
  models: ModelsMap,
  modelUrn: string,
  dbIds: number[]
): Source<DbIdWithIfcClass | DbIdWithRevitCategory> => {
  return merge<DbIdWithIfcClass | DbIdWithRevitCategory>([
    getIfcClasses$(models, modelUrn, dbIds),
    getRevitCategories$(models, modelUrn, dbIds),
  ]);
};

type DbIdWithIfcClass = {
  dbId: number;
  modelUrn: string;
  ifcClass: IfcClass;
};

export async function getCategories(
  models: ModelsMap,
  modelDbIds: ElementIdentifierDbidType[]
): Promise<Array<DbIdWithIfcClass | DbIdWithRevitCategory>> {
  const categories: Array<DbIdWithIfcClass | DbIdWithRevitCategory> = [];
  for (const { modelUrn, dbIds } of modelDbIds) {
    if (dbIds.length === 0) {
      // Because forge returns properties for all elements when no dbIds are specified
      continue;
    }
    const model = models[modelUrn];
    const properties: Autodesk.Viewing.PropertyResult[] = await new Promise(
      (resolve, reject) => {
        model.getBulkProperties2(
          dbIds,
          {
            propFilter: ['IfcClass', 'Category'],
            categoryFilter: ['Element', '__category__', 'IFC Attributes'],
            ignoreHidden: false,
          },
          resolve,
          () => void reject('Failed to get properties')
        );
      }
    );
    properties.forEach((property) => {
      const ifcClass = property.properties.find(
        (prop) => prop.attributeName === 'IfcClass'
      )?.displayValue;

      if (ifcClass) {
        categories.push({
          dbId: property.dbId,
          modelUrn: modelUrn,
          ifcClass,
        } as DbIdWithIfcClass);
      } else {
        const revitCategory = property.properties.find(
          (prop) => prop.attributeName === 'Category'
        )?.displayValue;

        if (revitCategory) {
          categories.push({
            dbId: property.dbId,
            modelUrn: modelUrn,
            revitCategory: revitCategory,
          } as DbIdWithRevitCategory);
        }
      }
    });
  }

  return categories;
}

function toOperationResult(
  result: GeometricResult,
  context: OperationContext
): OperationResult {
  if (result === undefined) {
    return [undefined, context];
  }
  const dimensions = dimensionsForResultType[result.type];
  const { value, units } = getScaledValue(result.value, context, dimensions);
  return [
    {
      ...result,
      value: value,
      units,
    },
    context,
  ];
}
